import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { IDSBadge, IDSTextarea, IDSAccordion, IDSRadio, IDSRadioGroup, IDSCheckbox, IDSBreadcrumbs, IDSCard, IDSButton, IDSInput, IDSDialog } from "@inera/ids-react";
import InfoTooltip from "../../common/InfoTooltip";
import MarkdownContent from "../../common/MarkdownContent";
import LastUpdated from "../../common/LastUpdated";
import CommentThread from "../CommentThread";
import QuestionCommentThread from "../QuestionCommentThread";
import TestStepDetails from "../../testclient/TestStepDetails";
import Collapse from "../../Collapse";
import { saveQuestionAnswers } from "../../../services/testResultsAPI";
import { formatDateTimeNoSeconds } from "../../../utils/dateTime";

const READONLY_TEXTAREA_STYLE = {
  color: "var(--ids-palette-neutral-20, #1e1e1e)",
  backgroundColor: "var(--ids-palette-neutral-100, #ffffff)",
  opacity: 1,
  cursor: "default",
  minHeight: "11rem",
  height: "11rem",
  resize: "vertical",
};

// Question types whose answer is free text stored in answerText, not a yes/no.
const TEXT_VALUE_TYPES = ["TEXT", "DATE", "SOURCE_SYSTEM", "SERVICE_PROVIDER"];
const isTextValueType = (questionType) => TEXT_VALUE_TYPES.includes(questionType);

const testResultBadge = (status) => {
  if (status === "FINISHED") return <IDSBadge type="success">Lyckad</IDSBadge>;
  if (status === "FAILED") return <IDSBadge type="error">Misslyckad</IDSBadge>;
  return <IDSBadge type="info">{status}</IDSBadge>;
};

/**
 * Breadcrumb + title + status badge. `title` and the reports breadcrumb label
 * are role-specific and supplied by the caller.
 */
export const SubmissionHeader = ({ report, badge, breadcrumbLabel, title, showReviewerContact = false }) => (
  <>
    <div className="ids-mb-4">
      <IDSBreadcrumbs lead="Du är här:">
        <Link to="/start">Start</Link>
        <Link to="/testrapporter">{breadcrumbLabel}</Link>
        <span aria-current="page">{title}</span>
      </IDSBreadcrumbs>
    </div>

    <div className="ids-flex ids-mt-4" style={{ alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
      <h1 className="ids-heading-l" style={{ margin: 0 }}>{title}</h1>
      {badge && (
        <IDSBadge type={badge.type}>{badge.label}{showReviewerContact && report.reviewedByUserEmail && ` - ${report.reviewedByUserEmail}`}</IDSBadge>
      )}
    </div>
  </>
);

/** Metadata card describing the submission (who, org, contract, when, scenario). */
export const SubmissionMeta = ({ report, reviewScenario }) => (
  <IDSCard className="ids-mt-4">
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
      <h2 className="ids-heading-s" style={{ margin: 0 }}>Om inlämningen</h2>
      <IDSBadge type={reviewScenario === "webapp" ? "info" : "neutral"}>
        {reviewScenario === "webapp"
          ? "Testrapport"
          : "Självdeklaration"}
      </IDSBadge>
    </div>
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        rowGap: "0.5rem",
        columnGap: "1.25rem",
        margin: 0,
        fontSize: "0.9rem",
      }}
    >
      <dt style={{ fontWeight: 600 }}>Inskickad av</dt>
      <dd style={{ margin: 0 }}>
        {report.submittedByUserName || "—"}
        {report.submittedByUserEmail && (
          <span style={{ color: "var(--ids-palette-neutral-40)" }}> · {report.submittedByUserEmail}</span>
        )}
      </dd>

      <dt style={{ fontWeight: 600 }}>Organisation</dt>
      <dd style={{ margin: 0 }}>{report.organisationName || "—"}</dd>

      <dt style={{ fontWeight: 600 }}>Tjänstekontrakt</dt>
      <dd style={{ margin: 0 }}>{report.selectedContract || report.contractName || "—"}</dd>

      <dt style={{ fontWeight: 600 }}>Inskickad</dt>
      <dd style={{ margin: 0 }}>{formatDateTimeNoSeconds(report.date, "—")}</dd>
    </dl>
  </IDSCard>
);

/** Read-only "Genomförda tester" section with per-test comment threads. */
export const SubmissionTestResults = ({ report, groupedTests, userRole, canAddComments = false, onTestResultCommentsChanged }) => {
  const [showOnlyFailed, setShowOnlyFailed] = useState(false);
  const [showOnlyWithComments, setShowOnlyWithComments] = useState(false);
  // Content shown in the request/response dialog ({ title, content }).
  const [dialogContent, setDialogContent] = useState(null);
  if ((report.testResults || []).length === 0) return null;

  // A test has a reviewer comment if any comment is not authored by a tester.
  const testHasReviewerComment = (test) =>
    (test.comments || []).some(
      (c) => c.commentType === "REVIEWER_COMMENT" || (c.authorRole && c.authorRole !== "TESTER")
    );

  // A tester may only comment on a test the reviewer has already commented on;
  // the reviewer is unrestricted. Either way, only while comments are allowed.
  const canAddForTest = (test) =>
    canAddComments && (userRole === "REVIEWER" || testHasReviewerComment(test));

  // The "with comments" filter is only meaningful once the reviewer has left
  // at least one comment — hide it while the review is still ongoing.
  const hasAnyReviewerComments = Object.values(groupedTests).some((tests) => tests.some(testHasReviewerComment));

  return (
    <section style={{ marginTop: "2rem" }}>
      <h2 className="ids-heading-m">Genomförda tester</h2>
      <div className="ids-mt-2 ids-mb-4" style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center" }}>
        <IDSRadioGroup legend="Filtrera tester" hideLegend name={`test-filter-${report.id}`} compact
          onRadioChange={(e) => setShowOnlyFailed(e.target.value === "failed")}>
          <IDSRadio value="all" defaultChecked={!showOnlyFailed}>Alla</IDSRadio>
          <IDSRadio value="failed" defaultChecked={showOnlyFailed}>Misslyckade tester</IDSRadio>
        </IDSRadioGroup>
        {hasAnyReviewerComments && (
          <IDSCheckbox
            checked={showOnlyWithComments}
            onChange={() => setShowOnlyWithComments((v) => !v)}
          >
            Visa endast tester med kommentarer
          </IDSCheckbox>
        )}
      </div>
      {Object.entries(groupedTests)
        .map(([suiteName, tests]) => ({
          suiteName,
          tests: tests.filter((t) =>
            (!showOnlyFailed || t.status === "FAILED") &&
            (!showOnlyWithComments || (t.comments || []).length > 0)
          ),
        }))
        .filter(({ tests }) => (!showOnlyFailed && !showOnlyWithComments) || tests.length > 0)
        .map(({ suiteName, tests }) => (
          <div key={suiteName} className="ids-mb-4">
            <IDSAccordion headline={<h3>{suiteName}</h3>} headlineSize="s" level={2}>
              <div className="ids-mt-2">
                {tests.map((test) => (
                  <IDSCard key={test.id} className="ids-mb-3">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                      <strong>{test.testCase}</strong>
                      {testResultBadge(test.status)}
                    </div>
                    {!userRole || userRole !== "REVIEWER" ? (
                      <>
                        {test.status === "FAILED" && test.message && (
                          <div
                            className="ids-mt-2 ids-p-3"
                            style={{
                              backgroundColor: "#f6f1f1",
                              borderLeft: "4px solid #a61d15",
                              borderRadius: "0.25rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Error</strong>
                            <p style={{ margin: "0.5rem 0 0" }}>{test.message}</p>
                          </div>
                        )}
                        <TestStepDetails
                          steps={test.steps}
                          testCase={test.testCase}
                          onShowContent={setDialogContent}
                        />
                      </>
                    ) : null}
                    {test.justification && (
                      <div className="ids-mt-2">
                        <IDSTextarea block light readOnly label="Motivering" value={test.justification} rows={4} style={READONLY_TEXTAREA_STYLE} />
                      </div>
                    )}
                    <CommentThread testResultId={test.id} initialJustification={test.justification}
                      userRole={userRole} existingComments={test.comments || []}
                      canAdd={canAddForTest(test)}
                      onCommentsChanged={(comments) => onTestResultCommentsChanged?.(test.id, comments)} />
                  </IDSCard>
                ))}
              </div>
            </IDSAccordion>
          </div>
        ))}

      {/* Dialog for viewing a step's full request / response */}
      <IDSDialog
        show={!!dialogContent}
        headline={dialogContent?.title}
        dismissible
        maxWidth="800px"
        onVisibilityChange={(visible) => {
          if (!visible) setDialogContent(null);
        }}
      >
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
          {dialogContent?.content}
        </pre>
      </IDSDialog>
    </section>
  );
};

const JiraStatusBadge = ({ status }) => {
  const mapping = {
    Open:          { type: "neutral", label: "Öppen" },
    "In Progress": { type: "info", label: "Pågår" },
    Closed:        { type: "success", label: "Stängd" },
    Done:          { type: "success", label: "Klar" },
    Resolved:      { type: "success", label: "Löst" },
  };
  const s = mapping[status] || { type: "neutral", label: status };
  return <IDSBadge type={s.type}>{s.label}</IDSBadge>;
};

const JiraDecisionBadge = ({ decision }) => {
  if (!decision) return null;
  const mapping = {
    "Godkänd Avvikelse":      { type: "success",  label: "Godkänd avvikelse" },
    "Godkänd avvikelse":      { type: "success",  label: "Godkänd avvikelse" },
    "Godkänd med förbehåll":  { type: "info",     label: "Godkänd med förbehåll" },
    "Underkänd":              { type: "error",    label: "Underkänd" },
    "Ingen avvikelse":        { type: "neutral",  label: "Ingen avvikelse" },
  };
  const s = mapping[decision] || { type: "neutral", label: decision };
  return <IDSBadge type={s.type}>{s.label}</IDSBadge>;
};

/**
 * Read-only "Avvikelser" section: lists each deviation with its Jira status and
 * decision. `showJiraLink` (reviewer) renders the internal issue link; for the
 * tester the issue is shown as plain text. Renders nothing when there are no
 * deviations (i.e. deviation handling has not been started).
 */
export const SubmissionDeviations = ({ deviations = [], testResults = [], showJiraLink = false }) => {
  if (!deviations.length) return null;

  return (
    <section style={{ marginTop: "2rem" }}>
      <h2 className="ids-heading-m">Avvikelser</h2>
      <IDSCard className="ids-mt-2">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--ids-palette-neutral-90)" }}>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Testfall</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Ärende</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Status</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Beslut</th>
            </tr>
          </thead>
          <tbody>
            {deviations.map((dev) => {
              const testResult = testResults.find((tr) => tr.id === dev.testResultId);
              const hasExtra = dev.jiraDecisionJustification || dev.jiraDecisionDuedate;
              const issueLabel = dev.issueKey ?? (dev.issueLink ? dev.issueLink.split("/").pop() : "–");
              return (
                <tr key={dev.id} style={{ borderBottom: "1px solid var(--ids-palette-neutral-95)" }}>
                  <td style={{ padding: "4px 8px" }}>{testResult?.testCase ?? `TestResult #${dev.testResultId}`}</td>
                  <td style={{ padding: "4px 8px" }}>
                    {showJiraLink && dev.issueLink
                      ? <a href={dev.issueLink} target="_blank" rel="noreferrer">{issueLabel}</a>
                      : issueLabel}
                  </td>
                  <td style={{ padding: "4px 8px" }}>
                    {dev.jiraStatus && <JiraStatusBadge status={dev.jiraStatus} />}
                  </td>
                  <td style={{ padding: "4px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {dev.jiraDecision && <JiraDecisionBadge decision={dev.jiraDecision} />}
                      {hasExtra && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          {dev.jiraDecisionJustification && (
                            <span style={{ fontSize: "12px", color: "var(--ids-palette-neutral-40)", fontStyle: "italic" }}>
                              {dev.jiraDecisionJustification}
                            </span>
                          )}
                          {dev.jiraDecisionDuedate && (
                            <span style={{ fontSize: "12px", color: "var(--ids-palette-neutral-50)" }}>
                              {dev.jiraDecisionDuedate}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </IDSCard>
    </section>
  );
};

/**
 * Read-only "Självdeklaration" answers with per-question comment threads.
 * `showUnexpectedFilter` and the "Avviker" badge (via `isReviewer`) are the
 * reviewer-only bits; the caller decides.
 */
export const SubmissionAnswers = ({
  report,
  sections,
  commentsByQuestion,
  isReviewer,
  showUnexpectedFilter,
  unexpectedCount,
  canAddComments,
  canEditAnswers = false,
  reviewScenario = "webapp",
  onAdded,
  onDeleted,
}) => {
  const [showOnlyUnexpected, setShowOnlyUnexpected] = useState(false);
  const [showOnlyWithComments, setShowOnlyWithComments] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const showAllSectionsOpen = reviewScenario === "local";
  // Locally saved answer edits (during clarification), keyed by questionId.
  const [editedAnswers, setEditedAnswers] = useState({});
  // Timestamps returned when an answer is corrected here, keyed by sectionId.
  // Overrides the value loaded with the report so the display stays truthful
  // without reloading the whole report.
  const [savedAt, setSavedAt] = useState({});

  // A tester (non-reviewer) in the clarification round may only comment on
  // questions the reviewer has already commented on — not on the rest.
  // The reviewer is never restricted.
  const questionHasReviewerComment = (q) =>
    (commentsByQuestion[q.id] || []).some((c) => c.authorRole !== "TESTER");
  const canAddForQuestion = (q) =>
    canAddComments && (isReviewer || questionHasReviewerComment(q));

  // The tester may edit an answer only once the reviewer has commented on it.
  const canEditForQuestion = (q) =>
    canEditAnswers && q.questionType !== "TEXT_BLOCK" && questionHasReviewerComment(q);

  // Merge any locally saved edit over the original answer for display/persistence.
  const mergedQuestion = (q) => ({ ...q, ...(editedAnswers[q.id] || {}) });

  // Persist an edited answer. The whole section is re-sent (mirroring the
  // questionnaire) so unrelated answers in the section are preserved.
  const saveAnswer = async (section, question, draft) => {
    const answersForSection = (section.questions || [])
      .filter((q) => q.questionType !== "TEXT_BLOCK")
      .map((q) => {
        const cur = q.id === question.id ? draft : mergedQuestion(q);
        const isText = isTextValueType(q.questionType);
        if (isText) {
          return { questionId: q.id, answerBoolean: null, answerText: cur.answerText || "", motivation: null };
        }
        if (typeof cur.answerBoolean !== "boolean") return null;
        return {
          questionId: q.id,
          answerBoolean: cur.answerBoolean,
          answerText: cur.answerText || "",
          motivation: cur.motivation || null,
        };
      })
      .filter(Boolean);
    const saved = await saveQuestionAnswers(
      report.contractId,
      section.id,
      report.keycloakOrgId,
      { answers: answersForSection },
      // Write back to the submitting tester's answers — a reviewer must not create
      // a separate answer record of their own.
      report.submittedByUserId
    );
    setEditedAnswers((prev) => ({ ...prev, [question.id]: draft }));
    setSavedAt((prev) => ({ ...prev, [section.id]: saved?.updatedAt ?? new Date().toISOString() }));
  };

  // Sections actually shown after applying the active filters. Reused both for
  // rendering and for keeping the section navigator in sync (via the callback).
  const visibleSections = useMemo(
    () =>
      sections
        .map((s) => {
          let qs = s.questions || [];
          if (showOnlyUnexpected) qs = qs.filter((q) => q.isUnexpected);
          if (showOnlyWithComments) qs = qs.filter((q) => (commentsByQuestion[q.id] || []).length > 0);
          return { ...s, questions: qs };
        })
        .filter((s) => (!showOnlyUnexpected && !showOnlyWithComments) || (s.questions && s.questions.length > 0)),
    [sections, showOnlyUnexpected, showOnlyWithComments, commentsByQuestion]
  );

  const sectionUpdatedAt = (section) => savedAt[section.id] ?? section.answersUpdatedAt ?? null;

  // Most recent save across all sections, including edits made in this view.
  const latestUpdatedAt = useMemo(() => {
    const stamps = sections
      .map((s) => sectionUpdatedAt(s))
      .filter(Boolean)
      .map((t) => new Date(t).getTime())
      .filter((t) => !Number.isNaN(t));
    return stamps.length ? new Date(Math.max(...stamps)).toISOString() : (report.answersUpdatedAt ?? null);
  }, [sections, savedAt, report.answersUpdatedAt]);

  useEffect(() => {
    if (showAllSectionsOpen) {
      setOpenSections({});
      return;
    }

    setOpenSections((prev) => {
      const next = {};
      visibleSections.forEach((section, index) => {
        const wasOpen = !!prev[section.id];
        next[section.id] = wasOpen || index === 0;
      });
      return next;
    });
  }, [visibleSections, showAllSectionsOpen]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => {
      const next = {};
      visibleSections.forEach((section) => {
        next[section.id] = section.id === sectionId ? !prev[section.id] : false;
      });
      return next;
    });
  };

  return (
    <section style={{ marginTop: "2rem" }}>
      <h2 className="ids-heading-m">Självdeklaration</h2>
      {latestUpdatedAt && (
        <div className="ids-mt-1">
          <LastUpdated value={latestUpdatedAt} label="Svaren uppdaterades senast" />
        </div>
      )}
      <div className="ids-mt-2 ids-mb-4" style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center" }}>
        {showUnexpectedFilter && (
          <IDSRadioGroup legend="Filtrera frågor" hideLegend name={`q-filter-${report.id}`} compact
            onRadioChange={(e) => setShowOnlyUnexpected(e.target.value === "unexpected")}>
            <IDSRadio value="all" defaultChecked={!showOnlyUnexpected}>Visa alla</IDSRadio>
            <IDSRadio value="unexpected" defaultChecked={showOnlyUnexpected}>Visa oväntade svar{unexpectedCount > 0 && ` (${unexpectedCount})`}</IDSRadio>
          </IDSRadioGroup>
        )}
        <IDSCheckbox
          checked={showOnlyWithComments}
          onChange={() => setShowOnlyWithComments((v) => !v)}
        >
          Visa endast frågor med kommentarer
        </IDSCheckbox>
      </div>

      {sections.length === 0 && <p><em>Inga frågor besvarades för denna rapport.</em></p>}

      {showAllSectionsOpen ? (
        visibleSections.map((section) => (
          <IDSCard key={section.id} id={`reviewsection-${section.id}`} className="ids-mt-4">
            <h3 className="ids-heading-s">{section.title}</h3>
            {sectionUpdatedAt(section) && (
              <div className="ids-mb-2">
                <LastUpdated value={sectionUpdatedAt(section)} />
              </div>
            )}
            {section.description && (
              <p className="ids-body ids-mb-4" style={{ color: "var(--ids-palette-neutral-30)" }}>{section.description}</p>
            )}
            {(section.questions || [])
              .slice()
              .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
              .map((q) => (
                <div key={q.id} style={{ marginBottom: "1.5rem" }}>
                  {canEditForQuestion(q) ? (
                    <EditableQuestionAnswer
                      question={mergedQuestion(q)}
                      isReviewer={isReviewer}
                      onSave={(draft) => saveAnswer(section, q, draft)}
                    />
                  ) : (
                    <QuestionAnswer question={mergedQuestion(q)} isReviewer={isReviewer} />
                  )}
                  {q.questionType !== "TEXT_BLOCK" && (
                    <QuestionCommentThread
                      executionId={report.id}
                      questionId={q.id}
                      comments={commentsByQuestion[q.id] || []}
                      canAdd={canAddForQuestion(q)}
                      onAdded={(saved) => onAdded(q.id, saved)}
                      onDeleted={(commentId) => onDeleted(q.id, commentId)}
                    />
                  )}
                </div>
              ))}
          </IDSCard>
        ))
      ) : (
        visibleSections.map((section) => {
          const isOpen = !!openSections[section.id];
          return (
            <IDSCard
              key={section.id}
              id={`reviewsection-${section.id}`}
              className="ids-mt-3"
              style={{ overflow: isOpen ? "visible" : "hidden", position: "relative", zIndex: isOpen ? 5 : "auto" }}
            >
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
                aria-label={isOpen ? `Stäng avsnitt ${section.title}` : `Öppna avsnitt ${section.title}`}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: "0.5rem 0",
                  font: "inherit",
                }}
              >
                <span
                  aria-hidden="true"
                  className={isOpen ? "ids-icon-chevron-down" : "ids-icon-chevron-right"}
                  style={{ height: "1rem", width: "1rem" }}
                />
                <h3 className="ids-heading-m" style={{ margin: 0, flex: 1, overflowWrap: "anywhere" }}>
                  {section.title}
                </h3>
                <span style={{
                  fontSize: "0.75rem",
                  color: "var(--ids-palette-neutral-50)",
                  background: "var(--ids-palette-neutral-95, #f1f4f6)",
                  borderRadius: "999px",
                  padding: "0.2rem 0.5rem",
                }}>{(section.questions || []).length} frågor</span>
              </button>
              <Collapse open={isOpen}>
                <div style={{ borderTop: "1px solid var(--ids-palette-neutral-90, #dfe5ea)", paddingTop: "1rem" }}>
                  {sectionUpdatedAt(section) && (
                    <div className="ids-mb-2">
                      <LastUpdated value={sectionUpdatedAt(section)} />
                    </div>
                  )}
                  {section.description && (
                    <p className="ids-body ids-mb-4" style={{ color: "var(--ids-palette-neutral-30)", marginTop: 0 }}>{section.description}</p>
                  )}
                  {(section.questions || [])
                    .slice()
                    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                    .map((q) => (
                      <div key={q.id} style={{ marginBottom: "1.5rem" }}>
                        {canEditForQuestion(q) ? (
                          <EditableQuestionAnswer
                            question={mergedQuestion(q)}
                            isReviewer={isReviewer}
                            onSave={(draft) => saveAnswer(section, q, draft)}
                          />
                        ) : (
                          <QuestionAnswer question={mergedQuestion(q)} isReviewer={isReviewer} />
                        )}
                        {q.questionType !== "TEXT_BLOCK" && (
                          <QuestionCommentThread
                            executionId={report.id}
                            questionId={q.id}
                            comments={commentsByQuestion[q.id] || []}
                            canAdd={canAddForQuestion(q)}
                            onAdded={(saved) => onAdded(q.id, saved)}
                            onDeleted={(commentId) => onDeleted(q.id, commentId)}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </Collapse>
            </IDSCard>
          );
        })
      )}
    </section>
  );
};

/** Read-only rendering of a single question + its answer, by type. */
const QuestionAnswer = ({ question: q, isReviewer }) => {
  if (q.questionType === "TEXT_BLOCK") {
    return (
      <div style={{ borderLeft: "3px solid var(--ids-color-primary, #0065bd)", paddingLeft: "0.9rem" }}>
        <p style={{ fontWeight: 600, fontSize: "0.95rem", margin: 0, whiteSpace: "pre-line" }}>
          {q.questionTitle || q.questionText}
          <InfoTooltip info={q.info} />
        </p>
        {q.bodyText && (
          <MarkdownContent style={{ marginTop: "0.3rem", color: "var(--ids-palette-neutral-30)", fontSize: "0.875rem" }}>
            {q.bodyText}
          </MarkdownContent>
        )}
      </div>
    );
  }

  if (isTextValueType(q.questionType)) {
    const hasAnswer = q.answerText != null && String(q.answerText).trim() !== "";
    return (
      <>
        <p style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.2rem", whiteSpace: "pre-line" }}>
          {q.questionTitle || q.questionText}
          <InfoTooltip info={q.info} />
        </p>
        <IDSTextarea block light readOnly
          subtitle={q.questionType === "DATE" ? "Datum" : undefined}
          value={hasAnswer ? q.answerText : "(Inget svar angivet)"}
          rows={4}
          style={READONLY_TEXTAREA_STYLE} />
      </>
    );
  }

  // YES_NO
  const answered = q.answerBoolean === true || q.answerBoolean === false;
  const expected = q.expectedAnswerBoolean;
  const isUnexpected = q.isUnexpected || (answered && expected != null && q.answerBoolean !== expected);
  return (
    <div>
      {q.questionTitle && <p style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.2rem", whiteSpace: "pre-line" }}>{q.questionTitle}</p>}
      <p className="ids-body" style={{ marginBottom: "0.4rem", whiteSpace: "pre-line" }}>
        {q.questionText}
        <InfoTooltip info={q.info} />
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
        {!answered ? <IDSBadge type="neutral">Ej besvarad</IDSBadge>
          : q.answerBoolean ? <IDSBadge type="success">Ja</IDSBadge> : <IDSBadge type="error">Nej</IDSBadge>}
        {isUnexpected && isReviewer && <IDSBadge type="attention">Avviker från förväntat</IDSBadge>}
      </div>
      {q.answerText && (
        <CommentBox label={q.followupConfig?.text || "Följdfråga"} text={q.answerText} accent="#0065bd" />
      )}
      {q.motivation && <CommentBox label="Motivering" text={q.motivation} accent="#856404" />}
    </div>
  );
};

const isFollowupVisible = (q, answerBoolean) => {
  const cfg = q.followupConfig;
  if (!cfg || !cfg.trigger || cfg.trigger === "NONE") return false;
  if (cfg.trigger === "ON_YES") return answerBoolean === true;
  if (cfg.trigger === "ON_NO") return answerBoolean === false;
  return false;
};

const isAnswerUnexpected = (q, answerBoolean) =>
  q.expectedAnswerBoolean !== null &&
  q.expectedAnswerBoolean !== undefined &&
  answerBoolean !== null &&
  answerBoolean !== undefined &&
  answerBoolean !== q.expectedAnswerBoolean;

/**
 * Editable variant of a single question's answer, used during the clarification
 * round for questions the reviewer has commented on. Supports TEXT/DATE (free
 * text), YES_NO (Ja/Nej) plus its follow-up answer and motivation. Persists via
 * the `onSave` callback and shows a small saved/failed indicator.
 */
const EditableQuestionAnswer = ({ question: q, isReviewer, onSave }) => {
  const isText = isTextValueType(q.questionType);
  const [answerBoolean, setAnswerBoolean] = useState(
    typeof q.answerBoolean === "boolean" ? q.answerBoolean : null
  );
  const [answerText, setAnswerText] = useState(q.answerText || "");
  const [motivation, setMotivation] = useState(q.motivation || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const followupVisible = isFollowupVisible(q, answerBoolean);
  const unexpected = isAnswerUnexpected(q, answerBoolean);

  const markDirty = () => {
    setSaved(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const draft = isText
        ? { answerBoolean: null, answerText, motivation: null }
        : {
            answerBoolean,
            answerText: followupVisible ? answerText : "",
            motivation: unexpected ? motivation : null,
          };
      await onSave(draft);
      setSaved(true);
    } catch (e) {
      console.error("Could not save answer:", e);
      setError("Kunde inte spara svar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {q.questionTitle && (
        <p style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.2rem", whiteSpace: "pre-line" }}>{q.questionTitle}</p>
      )}
      <p className="ids-body" style={{ marginBottom: "0.4rem", whiteSpace: "pre-line" }}>
        {q.questionText}
        <InfoTooltip info={q.info} />
      </p>

      {isText ? (
        <IDSTextarea
          block
          rows={3}
          label={q.questionText}
          subtitle={q.questionType === "DATE" ? "Datum" : undefined}
          value={answerText}
          onInput={(e) => { setAnswerText(e.target.value); markDirty(); }}
          placeholder="Ange svar..."
        />
      ) : (
        <>
          <IDSRadioGroup
            legend={q.questionText}
            hideLegend
            name={`edit-q-${q.id}`}
            onRadioChange={(e) => { setAnswerBoolean(e.target.value === "true"); markDirty(); }}
          >
            <IDSRadio value="true" checked={answerBoolean === true}>Ja</IDSRadio>
            <IDSRadio value="false" checked={answerBoolean === false}>Nej</IDSRadio>
          </IDSRadioGroup>
          {unexpected && isReviewer && (
            <div className="ids-mt-2">
              <IDSBadge type="attention">Avviker från förväntat</IDSBadge>
            </div>
          )}
          {followupVisible && (
            <div className="ids-mt-3">
              <IDSInput
                label={q.followupConfig?.text || "Följdfråga"}
                type="text"
                maxLength={400}
                value={answerText}
                onChange={(e) => { setAnswerText(e.target.value); markDirty(); }}
                placeholder="Ange svar..."
              />
            </div>
          )}
          {unexpected && (
            <div className="ids-mt-3">
              <IDSTextarea
                block
                rows={3}
                label="Motivering"
                value={motivation}
                onInput={(e) => { setMotivation(e.target.value); markDirty(); }}
                placeholder="Beskriv varför svaret avviker från förväntat..."
              />
            </div>
          )}
        </>
      )}

      <div className="ids-mt-2" style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
        <IDSButton size="s" onClick={handleSave} disabled={saving}>Spara svar</IDSButton>
        {saved && <span style={{ fontSize: "0.85rem", color: "var(--ids-palette-success-40, #37823a)" }}>Sparat</span>}
        {error && <span style={{ fontSize: "0.85rem", color: "var(--ids-palette-error-40, #c73c30)" }}>{error}</span>}
      </div>
    </div>
  );
};

const CommentBox = ({ label, text, accent }) => (
  <div style={{ marginTop: "0.6rem", borderLeft: `3px solid ${accent}`, background: "var(--ids-palette-neutral-97, #f6f6f6)", padding: "0.5rem 0.8rem", borderRadius: "4px" }}>
    <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "var(--ids-palette-neutral-40)" }}>{label}</span>
    <span style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>{text}</span>
  </div>
);

